import { MediaMatcher } from '@angular/cdk/layout';
import { DOCUMENT, Injectable, Signal, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  Observable,
  distinctUntilChanged,
  fromEvent,
  map,
  shareReplay,
  startWith,
} from 'rxjs';

/**
 * Tells whether the device has a touchscreen, so touch-only affordances such
 * as list drag handles can be shown.
 *
 * The user agent is deliberately not used: iPadOS Safari identifies itself as
 * desktop Safari on macOS. `navigator.maxTouchPoints` and the
 * `(any-pointer: coarse)` media query both report the touchscreen even when a
 * keyboard, mouse or trackpad is also attached.
 */
@Injectable({ providedIn: 'root' })
export class TouchCapabilityService {
  static readonly COARSE_POINTER_QUERY = '(any-pointer: coarse)';

  private readonly document = inject(DOCUMENT);
  // MediaMatcher adds the empty CSS rule WebKit needs before it fires
  // `change` events for a media query.
  private readonly coarsePointerQuery = inject(MediaMatcher).matchMedia(
    TouchCapabilityService.COARSE_POINTER_QUERY,
  );

  /** Emits the current value on subscribe, then again whenever it changes. */
  readonly touchCapable$: Observable<boolean> = fromEvent(
    this.coarsePointerQuery,
    'change',
  ).pipe(
    startWith(undefined),
    map(() => this.detectTouchscreen()),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly isTouchCapable: Signal<boolean> = toSignal(this.touchCapable$, {
    requireSync: true,
  });

  private detectTouchscreen(): boolean {
    const maxTouchPoints =
      this.document.defaultView?.navigator.maxTouchPoints ?? 0;
    return maxTouchPoints > 0 || this.coarsePointerQuery.matches;
  }
}
